<template>
    <td
        role="cell"
        :data-p-editable-column="false"
        :data-p-frozen-column="frozen"
        :class="containerClass"
        :style="containerStyle"
    >
        <div class="md-editable-grid-cell-wrapper">
            <!-- ROW SELECT ICON -->
            <template v-if="!isAppendSection && $parentInstance.rowSelectionEnabled">
                <span
                    v-if="isSelected"
                    :id="`select-icon-${rowIndex}`"
                    class="rounded-circle tableActionBTN tableBTNtext checkedBTN ikon med-status-complete"
                    @click.stop="$emit('select-row', rowKey)"
                ></span>
                <span
                    v-else
                    :id="`select-icon-${rowIndex}`"
                    class="rounded-circle tableActionBTN tableBTNtext uncheckedBTN ikon med-status-complete"
                    @click.stop="$emit('select-row', rowKey)"
                ></span>
            </template>

            <template v-for="(action, idx) in visibleActions">
                <b-tooltip
                    :key="`tooltip-${rowIndex}-${idx}`"
                    v-if="action.tooltip"
                    :target="`action-${rowIndex}-${idx}`"
                    placement="top"
                    :delay="{ show: 1000, hide: 50 }"
                >
                    {{ action.tooltip }}
                </b-tooltip>
                <span
                    :key="`action-${rowIndex}-${idx}`"
                    :id="`action-${rowIndex}-${idx}`"
                    :disabled="actionDisabled(action, item)"
                    @click="() => handleActionClick(action, item, rowIndex)"
                    :class="`rounded-circle tableActionBTN tableBTNtext ikon ${action.icon}`"
                >
                </span>
            </template>
        </div>
    </td>
</template>

<script>
import DomHandler from '@/common/DOMUtils.js';
import ShowMessage from '@/components/messages/ShowMessage';

export default {
    name: 'EditableActionCell',
    mixins: [ShowMessage],
    inject: ['$parentInstance'],
    props: {
        actions: {
            type: Array,
            default: () => []
        },
        item: {
            type: Object,
            required: true
        },
        rowIndex: {
            type: Number,
            required: true
        },
        rowKey: {
            type: [String, Number],
            default: null
        },
        canExecuteAction: {
            Type: Function,
            required: true
        },
        isAppendSection: {
            Type: Boolean,
            default: false
        },
        isSelected: {
            type: Boolean,
            default: false
        },
        frozen: {
            type: Boolean,
            default: false
        },
        alignFrozen: {
            type: String,
            default: 'left',
            validator: value => ['left', 'right'].includes(value)
        }
    },
    data() {
        return {
            appendSectionActions: [
                {
                    id: 'save',
                    visible: true,
                    icon: 'med-save',
                    handler: this.saveHandler,
                    tooltip: this.$t('Shared.Save')
                },
                {
                    id: 'delete',
                    visible: true,
                    icon: 'med-bin',
                    handler: this.deleteHandler,
                    tooltip: this.$tc('Shared.DelRecord', 1)
                }
            ],
            styleObject: {}
        };
    },
    computed: {
        visibleActions() {
            if (this.isAppendSection) {
                return this.appendSectionActions;
            }

            return this.actions.filter(action => this.actionVisible(action, this.item));
        },
        containerStyle() {
            let style = this.frozen ? [this.styleObject] : [];

            return style;
        },
        containerClass() {
            const columnClass = 'md-editable-grid-cell-editable';

            let frozenClass = '';
            if (this.frozen) {
                frozenClass = 'table-frozen-column';
            }

            return [columnClass, frozenClass].filter(x => x.length !== 0);
        }
    },
    methods: {
        actionVisible(action, item) {
            return this.$parentInstance.actionVisible(action, item);
        },
        actionDisabled(action, item) {
            if (typeof action.disabled === 'function') {
                try {
                    return action.disabled(item);
                } catch (e) {
                    return false;
                }
            }

            return !!action.disabled;
        },
        handleActionClick(action, item, index) {
            if (this.actionDisabled(action, item)) {
                return;
            }

            if (typeof action.handler === 'function') {
                if (action.id === 'delete') {
                    // Deleting a row shouldn't be blocked by validation errors in its cells.
                    if (this.isAppendSection) {
                        action.handler(index);
                    } else {
                        action.handler(item, index);
                    }
                } else if (this.canExecuteAction(index)) {
                    action.handler(item, index);
                } else {
                    this.showMessage(
                        this.$tc('Msg.Warning', 1),
                        this.$t('Msg.InvalidItemAction'),
                        'warning'
                    );
                }
            }
        },
        deleteHandler(rowIndex) {
            this.$bvModal
                .msgBoxConfirm(this.$t('Msg.DeleteAllRecord'), {
                    title: this.$tc('Shared.DelRecord', 1),
                    okTitle: this.$t('Shared.TitleDelete'),
                    cancelTitle: this.$t('Shared.BtnCancel'),
                    footerClass: 'p-2',
                    titleClass: 'modal-title modal-type-warning'
                })
                .then(value => {
                    if (value) {
                        this.$parentInstance.d_new_rows.splice(rowIndex, 1);
                    }
                });
        },

        async saveHandler(row, rowIdex) {
            await this.$parentInstance.saveNewRow(row, rowIdex);
        },

        updateStickyPosition() {
            if (this.frozen) {
                let align = this.alignFrozen;

                if (align === 'right') {
                    let right = 0;
                    let next = DomHandler.getNextElementSibling(
                        this.$el,
                        '[data-p-frozen-column="true"]'
                    );

                    if (next) {
                        right = DomHandler.getOuterWidth(next) + parseFloat(next.style.right || 0);
                    }

                    this.$set(this.styleObject, 'insetInlineEnd', right + 'px');
                } else {
                    let left = 0;
                    let prev = DomHandler.getPreviousElementSibling(
                        this.$el,
                        '[data-p-frozen-column="true"]'
                    );

                    if (prev) {
                        left = DomHandler.getOuterWidth(prev) + parseFloat(prev.style.left || 0);
                    }

                    this.$set(this.styleObject, 'insetInlineStart', left + 'px');
                }
            }
        }
    },

    mounted() {
        if (this.frozen) {
            this.updateStickyPosition();
        }
    },

    updated() {
        if (this.frozen) {
            this.updateStickyPosition();
        }
    }
};
</script>
